import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import type { OrderListItem, OrderStatus } from '@fitting-room/contracts'
import { Plus, Search, TriangleAlert } from 'lucide-react'
import { AssigneeFilterPopover } from '../../components/admin/AssigneeFilterPopover'
import { Chip } from '../../components/admin/Chip'
import { PageHeader } from '../../components/admin/PageHeader'
import { StaffMark } from '../../components/admin/StaffMark'
import { OrderStatusBadge } from '../../components/admin/orders/OrderStatusBadge'
import { PaymentStateBadge } from '../../components/admin/payments/PaymentStateBadge'
import { useAssignees, useOrders, useSupplierRefreshOnOpen } from '../../lib/adminQueries'
import { assigneeName } from '../../lib/appointments'
import { BOUTIQUE_LOCALE, boutiqueDayOf } from '../../lib/boutique'
import { formatDay } from '../../lib/format'
import { fulfillmentLine } from '../../lib/fulfillment'
import {
  ORDER_FLAG_HINT,
  ORDER_FLAG_LABEL,
  ORDER_FLAG_ORDER,
  ORDER_KIND_LABEL,
  ORDER_KIND_ORDER,
  knownValues,
  toggleValue,
} from '../../lib/orderFilters'
import { ORDER_STATUS_LABEL, ORDER_STATUS_ORDER, formatCents, lineTypeLabel, pickupAhead } from '../../lib/orders'
import { useStaffToken } from '../../lib/staffToken'
import { uiStrings } from '../../lib/strings'

const PAGE_SIZE = 25

function csv(raw: string | null): string[] {
  return (raw ?? '').split(',').filter(Boolean)
}

/**
 * Orders: tperent's `OrdersTab`, less everything rental — search, the status
 * chips, the list, pages of it. Newest first, because the order a stylist is
 * looking for is nearly always the one she just wrote or the one the customer
 * on the phone is asking about.
 *
 * The filters live in the URL, as the appointment list's do, so a filtered
 * list survives a reload and can be sent to a colleague. Beside the status
 * chips: where an order stands (Pending, Balance due, Not ordered, Not arrived yet, Ready
 * for pickup, Wedding within 60 days — each narrows the list further), the
 * kind of line on it (Special order, Off the rack — either) and the bridal
 * stylist. The API applies them all before it pages the list, and says how
 * many orders each chip would show on its own.
 */
export function OrdersPage() {
  const { token } = useStaffToken()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const statuses = useMemo(() => csv(params.get('status')), [params])
  const flags = useMemo(() => knownValues(params.get('show'), ORDER_FLAG_ORDER), [params])
  const kinds = useMemo(() => knownValues(params.get('kind'), ORDER_KIND_ORDER), [params])
  const stylists = useMemo(() => csv(params.get('stylist')), [params])
  const q = params.get('q') ?? ''
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1)
  const filters = useMemo(
    () => ({ statuses, flags, kinds, stylists, q, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    [statuses, flags, kinds, stylists, q, page],
  )
  const filtered = statuses.length + flags.length + kinds.length + stylists.length > 0
  const list = useOrders(token, filters)
  // Special orders linked to Jovita catch up as the list opens (at most every ten minutes).
  useSupplierRefreshOnOpen(token)
  const counts = list.data?.counts
  const members = useAssignees(token).data?.members ?? []
  const total = list.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Every change starts from the address bar as it is now, not from this
  // render: React Router renders a navigation in a transition, so a chip
  // pressed a moment ago may not have reached `params` yet — and a search, a
  // stylist, a page or Clear built from the older URL would undo it.
  const setParam = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(window.location.search)
    for (const [key, value] of Object.entries(updates)) {
      if (value === undefined || value === '') next.delete(key)
      else next.set(key, value)
    }
    setParams(next, { replace: true })
  }

  const toggleIn = <T extends string>(key: 'status' | 'show' | 'kind', value: T, order: readonly T[]) => {
    const next = new URLSearchParams(window.location.search)
    const list = toggleValue(knownValues(next.get(key), order), value, order)
    if (list.length > 0) next.set(key, list.join(','))
    else next.delete(key)
    next.delete('page')
    setParams(next, { replace: true })
  }
  const toggleStatus = (status: OrderStatus) => toggleIn('status', status, ORDER_STATUS_ORDER)

  return (
    <div>
      <PageHeader eyebrow="What customers bought" title="Orders">
        <button type="button" className="st-btn-ghost" onClick={() => void navigate('/admin/orders/jovita')}>
          Jovita orders
        </button>
        <button type="button" className="st-btn" onClick={() => void navigate('/admin/orders/new')}>
          <Plus size={14} aria-hidden />
          New order
        </button>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-2.5">
        <SearchBox value={q} onChange={(next) => setParam({ q: next, page: undefined })} />
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by status">
          {ORDER_STATUS_ORDER.map((status) => (
            <Chip key={status} pressed={statuses.includes(status)} onClick={() => toggleStatus(status)}>
              {ORDER_STATUS_LABEL[status]}
            </Chip>
          ))}
          <span className="st-meta ml-auto text-xs" aria-live="polite">
            {list.data ? `${total} ${total === 1 ? 'order' : 'orders'}` : list.isFetching ? 'Loading…' : ''}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by where it stands">
          {ORDER_FLAG_ORDER.map((flag) => (
            <Chip
              key={flag}
              pressed={flags.includes(flag)}
              count={counts?.[flag]}
              title={ORDER_FLAG_HINT[flag]}
              onClick={() => toggleIn('show', flag, ORDER_FLAG_ORDER)}
            >
              {ORDER_FLAG_LABEL[flag]}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by type and bridal stylist">
          {ORDER_KIND_ORDER.map((kind) => (
            <Chip
              key={kind}
              pressed={kinds.includes(kind)}
              count={counts?.[kind]}
              onClick={() => toggleIn('kind', kind, ORDER_KIND_ORDER)}
            >
              {ORDER_KIND_LABEL[kind]}
            </Chip>
          ))}
          <AssigneeFilterPopover
            members={members}
            selected={stylists}
            onChange={(next) => setParam({ stylist: next.join(','), page: undefined })}
          />
          {filtered ? (
            <button
              type="button"
              className="st-meta ml-1 text-xs underline"
              onClick={() => setParam({ status: undefined, show: undefined, kind: undefined, stylist: undefined, page: undefined })}
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      {list.isError ? <p className="st-alert mb-4">Couldn&rsquo;t load the orders. Check your sign-in and try again.</p> : null}
      {list.isPending ? <p className="st-meta">Loading…</p> : null}

      {list.data && list.data.orders.length === 0 ? (
        <div className="st-card px-4 py-10 text-center">
          <p className="st-meta">
            {q
              ? 'No order matches. Try the number without the prefix, or the customer’s phone.'
              : filtered
                ? 'No order matches these filters.'
                : 'No orders yet. Write one from an appointment, or with New order.'}
          </p>
        </div>
      ) : null}

      {(list.data?.orders ?? []).map((order) => (
        <OrderRow
          key={order.id}
          order={order}
          staffName={assigneeName(members, order.salesStaffId)}
          staffMark={members.find((m) => m.id === order.salesStaffId)?.calendarMark}
        />
      ))}

      {pages > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button type="button" className="st-btn-ghost" disabled={page <= 1} onClick={() => setParam({ page: String(page - 1) })}>
            Previous
          </button>
          <span className="st-meta text-xs tabular-nums">
            {page} of {pages}
          </span>
          <button type="button" className="st-btn-ghost" disabled={page >= pages} onClick={() => setParam({ page: String(page + 1) })}>
            Next
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * One order, a row you can hit with a thumb: the number and the name first,
 * then what and how much, then where its gowns are. An order with alerts
 * says how many beside its status, red when one of them is critical.
 */
function OrderRow({
  order,
  staffName,
  staffMark,
}: {
  readonly order: OrderListItem
  readonly staffName: string | undefined
  readonly staffMark: string | undefined
}) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const kindWords = [...new Set(order.lineKinds.map(lineTypeLabel))].filter((word) => word !== '—').join(', ')
  return (
    <Link
      to={`/admin/orders/${order.id}`}
      className="st-card-action st-row mb-2 block px-4 py-3"
      data-order-id={order.id}
      data-status={order.status}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="flex min-w-0 items-baseline gap-3">
          <span className="st-display shrink-0 text-lg tabular-nums">{order.orderNo}</span>
          <span className="st-row-name truncate font-medium">{order.customerName}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="tabular-nums">{formatCents(order.totalMinor)}</span>
          {/* A canceled order owes nothing, so its state against the total says nothing true. */}
          {order.lineCount > 0 && order.status !== 'canceled' ? <PaymentStateBadge state={order.paymentState} totalMinor={order.totalMinor} paidMinor={order.paidMinor} /> : null}
          {order.alerts.total > 0 ? (
            <span
              className="st-badge st-alert-kind"
              data-severity={order.alerts.critical > 0 ? 'critical' : 'warning'}
              data-testid="order-row-alerts"
              aria-label={`${order.alerts.total} ${order.alerts.total === 1 ? 'alert' : 'alerts'}`}
            >
              <TriangleAlert size={11} className="mr-1" aria-hidden />
              {order.alerts.total}
            </span>
          ) : null}
          <OrderStatusBadge status={order.status} />
        </div>
      </div>
      <div className="st-meta mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {/* What is on it by style number, and how it is bought — the shop's two questions about an order. */}
        <span data-testid="order-row-lines">
          {order.lineCount === 0
            ? 'Nothing on it yet'
            : [order.summary, kindWords].filter(Boolean).join(' · ')}
        </span>
        {order.shopifyOrderNos.length > 0 ? (
          <span className="tabular-nums" data-testid="order-row-shopify">
            Shopify {order.shopifyOrderNos.map((no) => `#${no}`).join(', ')}
          </span>
        ) : null}
        {order.notOrdered > 0 ? (
          <span className="st-badge st-alert-kind" data-severity="critical" data-testid="order-row-not-ordered">
            {t.orderRowNotOrdered(order.notOrdered)}
          </span>
        ) : null}
        {order.status !== 'canceled' && fulfillmentLine(order.fulfillment) ? (
          <span data-testid="order-row-fulfillment">{fulfillmentLine(order.fulfillment)}</span>
        ) : null}
        {order.status === 'canceled' ? (
          order.paidMinor > 0 ? <span className="tabular-nums">{formatCents(order.paidMinor)} still paid in</span> : null
        ) : order.balanceMinor > 0 ? (
          <span className="tabular-nums" data-testid="order-row-balance">
            {formatCents(order.balanceMinor)} due
          </span>
        ) : order.balanceMinor < 0 ? (
          <span className="tabular-nums">{formatCents(-order.balanceMinor)} overpaid</span>
        ) : null}
        {order.customerPhone ? <span className="tabular-nums">{order.customerPhone}</span> : null}
        {/* Until everything has gone home: set by hand or worked out, the day they're expected. */}
        {pickupAhead(order) ? (
          <span data-testid="order-row-pickup">Pickup {formatDay(order.expectedPickup.date, BOUTIQUE_LOCALE)}</span>
        ) : null}
        {order.weddingDate ? <span>Wedding {formatDay(order.weddingDate, BOUTIQUE_LOCALE)}</span> : null}
        <span>Written {formatDay(boutiqueDayOf(order.createdAt), BOUTIQUE_LOCALE)}</span>
        <span className="ml-auto flex items-center gap-1">
          {order.salesStaffId ? (
            <>
              <StaffMark mark={staffMark} name={staffName} />
              {staffName}
            </>
          ) : (
            <span className="text-[color:var(--st-ink-faint)]">No bridal stylist</span>
          )}
        </span>
      </div>
    </Link>
  )
}

/** Search, debounced into the URL, as the appointment list's is. */
function SearchBox({ value, onChange }: { readonly value: string; readonly onChange: (q: string) => void }) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  useEffect(() => {
    if (draft === value) return
    const timer = setTimeout(() => onChange(draft.trim()), 300)
    return () => clearTimeout(timer)
    // `onChange` is a fresh closure every render; the draft is what matters.
  }, [draft])
  return (
    <label className="relative block">
      <Search size={14} className="st-meta pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" aria-hidden />
      <input
        className="st-input st-input-search w-full pl-8"
        type="search"
        placeholder="Search order #, Shopify order #, name, phone or email"
        aria-label="Search orders"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
    </label>
  )
}
